import { format } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import { columnsFor, EscposText } from './escposText'
import { PrintStyle, useStarLine } from './printStyle'
import { StarImpactText } from './starImpact'
import { StarLineText } from './starLine'
import { groupByCategory, groupKitchenItems, KitchenGroup, kitchenMarkPlacement, noteDisplay, waiterSeats, MarkPlacement } from './tableTicket'
import { buildCupLabelLines, findCupLabelTemplate, paperForCupLabel } from './cupLabelTemplate'
import { guestSummary, storeGuestGroups } from '../guestGroups'

type SlipStyle = Pick<PrintStyle, 'fontWidth' | 'fontHeight' | 'brand' | 'starEscpos'>
type SlipWriter = EscposText | StarLineText | StarImpactText

function dotted(out: SlipWriter) {
    out.line('.'.repeat(columnsFor(1)), 1, 1, 1)
}

function writeMarkLine(out: SlipWriter, group: KitchenGroup, placement: MarkPlacement, width: number, height: number, withPrice: boolean) {
    const marks = group.marks.join(' ')
    const place = kitchenMarkPlacement(placement, Boolean(marks))
    const qty = String(group.amount)
    const name = group.name
    const price = withPrice ? `$${(group.price * group.amount).toFixed(2)}` : ''
    if (place === 'above' && marks) out.boldLine(marks, 0, width, height)
    if (place === 'beginning' && marks) {
        out.lineParts([{ text: `${marks}  `, bold: true }, { text: `${qty} ${name}` }], 0, width, height)
        if (price) out.item('', '', price, width, height)
    } else if (place === 'end' && marks) {
        out.item(qty, name, price ? `${marks} ${price}` : marks, width, height)
    } else {
        out.item(qty, name, price, width, height)
    }
    if (place === 'below' && marks) out.boldLine(marks, 0, width, height)
    group.options.forEach((option) => out.line(`  - ${option}`, 0, width, height))
    if (group.itemNote) out.line(group.itemNote, 0, width, height)
    if (group.seatNote) out.line(group.seatNote, 0, width, height)
    dotted(out)
}

function writeFireHeading(out: SlipWriter, heading: string, width: number, height: number, seen: { value: string }) {
    if (!heading || heading === seen.value) return
    seen.value = heading
    out.centered(heading, width, Math.min(8, height + 1))
    dotted(out)
}

function writeTableGroups(out: SlipWriter, order: any, groups: KitchenGroup[], width: number, height: number, withPrice: boolean) {
    const tableNote = noteDisplay(order?.tableNote || groups[0]?.item?.tableNote)
    if (tableNote) {
        tableNote.split('\n').forEach((line) => out.line(line, 0, width, height))
        out.rule(width, height)
    }
    const placement = order?.tableMarkPlacement
    const seen = { value: "" }
    groups.forEach((group) => {
        if (group.item?.fireHeadingOnly) {
            writeFireHeading(out, String(group.item.name || ""), width, height, seen)
            return
        }
        writeFireHeading(out, String(group.item?.fireHeading || ""), width, height, seen)
        writeMarkLine(out, group, placement, width, height, withPrice)
    })
}

function optionLines(item: any, withPrice: boolean): string[] {
    const lines: string[] = []
    const groups = item?.advancedOptions?.selections
    if (!Array.isArray(groups)) return lines
    groups.forEach((group: any) => {
        const optionsMap = group?.optionSet?.options
            ? new Map(group.optionSet.options.map((opt: any) => [opt.name, opt]))
            : new Map()
        Object.entries(group?.selections || {}).forEach(([name, opt]: [string, any]) => {
            if (!opt || opt.userQuantity <= 0) return
            const displayName = String((optionsMap.get(name) as any)?.printName || '').trim() || name
            let text = opt.userQuantity > 1 ? `${opt.userQuantity} x ${displayName}` : displayName
            if (withPrice && opt.price > 0) text += ` $${Number(opt.total || 0).toFixed(2)}`
            lines.push(text)
        })
    })
    return lines
}

function clock(timeZone?: string, pattern = 'HH:mm M/d'): string {
    try {
        return format(utcToZonedTime(new Date(), timeZone || 'UTC'), pattern)
    } catch {
        return format(new Date(), pattern)
    }
}

function open(printer: any, style: SlipStyle) {
    const emptyLines = Number(printer?.empty_lines_on_top) || 0
    const out = style.brand === 'StarImpact'
        ? new StarImpactText(emptyLines)
        : useStarLine(style)
            ? new StarLineText(emptyLines)
            : new EscposText(emptyLines)
    const width = style.fontWidth || 1
    const height = style.fontHeight || 1
    const titleH = Math.min(8, height + 1)
    return { out, width, height, titleH }
}

function dishInstruction(item: any): string {
    if (typeof item?.special_instructions === 'string') return item.special_instructions.trim()
    return ''
}

function writeOrderInstruction(out: SlipWriter, order: any, width: number, height: number) {
    const direct = typeof order?.special_instructions === 'string' ? order.special_instructions : order?.uberSlip?.special_instructions
    const note = typeof direct === 'string' ? direct.trim() : ''
    if (!note) return
    const limit = columnsFor(width)
    note.split('\n').forEach((raw) => {
        const words = String(raw).split(' ')
        let line = ''
        words.forEach((word) => {
            const next = line ? `${line} ${word}` : word
            if (next.length > limit && line) {
                out.boldLine(line, 0, width, height)
                line = word
            } else {
                line = next
            }
        })
        if (line) out.boldLine(line, 0, width, height)
    })
    out.rule(width, height)
}

function writeItems(out: SlipWriter, items: any[], width: number, height: number, withPrice: boolean) {
    const seen = { value: "" }
    items.forEach((item) => {
        if (item?.fireHeadingOnly) {
            writeFireHeading(out, String(item.name || ""), width, height, seen)
            return
        }
        writeFireHeading(out, String(item?.fireHeading || ""), width, height, seen)
        const dishNote = typeof item?.special_instructions === 'string'
        const legacyNote = dishNote ? '' : String(item?.itemNote || '').trim()
        const name = `${item.printName || item.name || ''}${legacyNote ? ` [${legacyNote}]` : ''}`
        const qty = String(item.amount ?? item.qty ?? 1)
        let right = ''
        if (withPrice) {
            let optionTotal = 0
            for (const group of item.advancedOptions?.selections || []) {
                for (const opt of Object.values(group?.selections || {}) as any[]) {
                    if (opt?.userQuantity > 0) optionTotal += Number(opt.total) || 0
                }
            }
            const unit = Number(item.price || 0) + optionTotal
            right = `$${(unit * Number(qty)).toFixed(2)}`
        }
        out.item(qty, name, right, width, height)
        optionLines(item, withPrice).forEach((option) => {
            out.line(`  - ${option}`, 0, width, height)
        })
        const note = dishInstruction(item)
        if (note) out.line(note, 0, width, height)
        out.line('', 0, width, height)
    })
}

function centerAndRight(out: SlipWriter, center: string, right: string, width: number, height: number) {
    const columns = columnsFor(width)
    const rightText = String(right || '')
    const centerText = String(center || '')
    const room = Math.max(0, columns - rightText.length - 1)
    const start = Math.max(0, Math.floor((room - centerText.length) / 2))
    const left = `${' '.repeat(start)}${centerText}`.slice(0, Math.max(room, 0))
    out.pair(left, rightText, width, height)
}

function kitchenHeader(out: SlipWriter, order: any, store: any, width: number, height: number, _titleH: number) {
    if (order?.uberSlip) {
        const slip = order.uberSlip
        const orderNumber = String(slip.orderNumber || '').trim()
        centerAndRight(out, orderNumber, String(slip.title || 'Uber'), width, height)
        const left = String(slip.customerName || '').trim()
        out.pair(left, clock(store?.timeZone, 'HH:mm M/d'), width, height)
        out.rule(width, height)
        return
    }
    if (order?.isTableOrder) out.boldLine('DINE-IN', 0, 2, 2)
    const tableOrder = Boolean(order?.isTableOrder)
    if (!tableOrder) {
        out.boldLine('Pour Emporter', 0, 1, 2)
        out.line('', 0, 1, 1)
    }
    const table = String((tableOrder && order.seatTableName) || order.tableName || store?.tableName || 'Counter')
    const count = String(order.counter || '').slice(-3)
    const guests = order.guestCount?.total
    const wide = table.length > 8 ? 1 : 2
    const tall = tableOrder ? (table.length > 8 ? 2 : 3) : 2
    out.line(table, 0, wide, tall)
    const server = String(order?.items?.find((item: any) => item?.posUserName)?.posUserName || "").trim().slice(0, 10)
    if (server && order?.isTableOrder) out.centered(server, 1, 1)
    const meta = [clock(store?.timeZone, 'M/d/yyyy'), clock(store?.timeZone, 'HH:mm'), count ? `#${count}` : '', guests ? String(guests) : ''].filter(Boolean).join('  ')
    out.line(meta, 2, 1, 1)
    dotted(out)
}

export function kitchenSlipText(order: any, store: any, printer: any, style: SlipStyle, withPrice = false): Uint8Array[] {
    if (order?.isTableOrder) {
        const tableName = order.seatTableName || order.tableName || ''
        const groups = groupKitchenItems(order.items || [], tableName, true)
        const slips = order?.cutbyItem
            ? groups.map((group) => [group])
            : order?.cutbyCategory
                ? groupByCategory(groups).map((entry) => entry[1])
                : [groups]
        return slips.map((slipGroups) => {
            const { out, width, height, titleH } = open(printer, style)
            kitchenHeader(out, order, store, width, height, titleH)
            writeOrderInstruction(out, order, width, height)
            if (order?.cutbyCategory && slipGroups[0]?.category) out.centered(slipGroups[0].category, width, titleH)
            writeTableGroups(out, order, slipGroups, width, height, withPrice)
            return out.finish(beep(printer))
        })
    }
    if (order?.cutbyItem) {
        return (order.items || []).map((item: any) => {
            const { out, width, height, titleH } = open(printer, style)
            kitchenHeader(out, order, store, width, height, titleH)
            writeOrderInstruction(out, order, width, height)
            writeItems(out, [item], width, height, withPrice)
            return out.finish(beep(printer))
        })
    }
    if (order?.cutbyCategory) {
        const groups = new Map<string, any[]>()
        ;(order.items || []).forEach((item: any) => {
            const category = item.category || 'Uncategorized'
            if (!groups.has(category)) groups.set(category, [])
            groups.get(category)!.push(item)
        })
        return Array.from(groups.entries()).map(([category, items]) => {
            const { out, width, height, titleH } = open(printer, style)
            kitchenHeader(out, order, store, width, height, titleH)
            writeOrderInstruction(out, order, width, height)
            out.centered(category, width, titleH)
            writeItems(out, items, width, height, withPrice)
            return out.finish(beep(printer))
        })
    }
    const { out, width, height, titleH } = open(printer, style)
    kitchenHeader(out, order, store, width, height, titleH)
    writeOrderInstruction(out, order, width, height)
    const notes = uniqueNotes((order.items || []).filter((item: any) => typeof item?.special_instructions !== "string"))
    notes.forEach((note) => out.line(note, 0, width, height))
    if (notes.length) out.rule(width, height)
    writeItems(out, order.items || [], width, height, withPrice)
    if (withPrice) {
        const subtotal = (order.items || []).reduce((sum: number, item: any) => {
            return sum + Number(item.price || 0) * Number(item.amount || 1)
        }, 0)
        out.rule(width, height)
        out.pair('Sub-Total', `$${(order.total > 0 ? order.total : subtotal).toFixed(2)}`, width, height)
    }
    return [out.finish(beep(printer))]
}

export function waiterSlipText(order: any, store: any, printer: any, style: SlipStyle): Uint8Array[] {
    if (!order?.isTableOrder || !order?.waiterSlip) return []
    const tableName = order.seatTableName || order.tableName || ''
    const seats = waiterSeats(order.items || [], tableName)
    const slips = order.waiterSlipCut === 'seat' ? seats.map((seat) => [seat]) : [seats]
    const placement = order.tableMarkPlacement
    return slips.map((seatList) => {
        const { out, width, height, titleH } = open(printer, style)
        kitchenHeader(out, order, store, width, height, titleH)
        const tableNote = noteDisplay(order.tableNote || order.items?.[0]?.tableNote)
        if (tableNote) {
            tableNote.split('\n').forEach((line) => out.line(line, 0, width, height))
            out.rule(width, height)
        }
        seatList.forEach((seat) => {
            if (seat.mark) out.boldLine(seat.mark, 0, width, titleH)
            if (seat.seatNote) out.line(seat.seatNote, 0, width, height)
            seat.items.forEach((item) => {
                const group: KitchenGroup = {
                    name: item.name,
                    amount: item.amount,
                    price: 0,
                    marks: placement ? item.marks : [],
                    itemNote: item.itemNote,
                    seatNote: '',
                    options: item.options,
                    category: '',
                    item: null,
                }
                writeMarkLine(out, group, placement, width, height, false)
            })
        })
        return out.finish(beep(printer))
    })
}

export function guestInitials(name: string): string {
    return String(name || '')
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .map((part) => `${part.charAt(0).toUpperCase()}.`)
        .join('')
}

export function deliveryAddressLines(address: any): string[] {
    if (!address) return []
    if (typeof address === 'string') return address.trim() ? [address.trim()] : []
    const street = address.street || address.line1 || address.address || address.original || ''
    const apartment = address.apartment || address.line2 || address.address2 || ''
    const city = address.city || ''
    const region = [address.state || address.province, address.postalCode || address.postCode || address.zip]
        .filter(Boolean)
        .join(' ')
    return [street, apartment, [city, region].filter(Boolean).join(', ')]
        .map((line) => String(line).trim())
        .filter(Boolean)
}

export function cupUnits(items: any[]): any[] {
    const cups: any[] = []
    ;(items || []).forEach((item) => {
        const qty = Math.max(1, Math.round(Number(item?.amount ?? item?.qty ?? 1)))
        for (let i = 0; i < qty; i++) cups.push(item)
    })
    return cups
}

function openCup(printer: any, style: SlipStyle, paper: string) {
    const linerless = paper === 'linerless'
    const top = linerless ? 0 : (Number(printer?.empty_lines_on_top) || 0) + 4
    return { ...open({ ...printer, empty_lines_on_top: top }, style), feeds: linerless ? 0 : 3 }
}

export function cupLabelText(order: any, store: any, printer: any, style: SlipStyle): Uint8Array[] {
    if (!order?.cupLabel) return []
    const cups = cupUnits(order.items || [])
    const total = cups.length
    const guest = String(order.guestName || '').trim()
    const when = clock(store?.timeZone, 'h:mm a')
    const template = findCupLabelTemplate(store, printer)
    return cups.map((item, index) => {
        if (template) {
            const { out, width, height, titleH, feeds } = openCup(printer, style, paperForCupLabel(template))
            buildCupLabelLines({
                template,
                item,
                order,
                store,
                index,
                total,
                timeText: when,
            }).forEach((line) => {
                if (line.bold) out.boldLine(line.text, 1, width, line.role === 'title' ? titleH : height)
                else out.centered(line.text, width, height)
            })
            return out.finish(beep(printer), feeds)
        }
        const { out, width, height, titleH, feeds } = openCup(printer, style, order.cupLabelPaper)
        if (guest) out.boldLine(guest, 1, width, titleH)
        out.centered(item.printName || item.name || '', width, height)
        optionLines(item, false).forEach((option) => out.centered(option, width, height))
        if (item.itemNote) out.centered(String(item.itemNote), width, height)
        out.centered(when, width, height)
        out.centered(`Cup ${index + 1} of ${total}`, width, height)
        return out.finish(beep(printer), feeds)
    })
}

export function bagLabelText(order: any, _store: any, printer: any, style: SlipStyle): Uint8Array[] {
    if (!order?.bagLabel) return []
    const { out, width, height, titleH } = open(printer, style)
    const guest = String(order.guestName || '').trim()
    if (guest) out.boldLine(guest, 1, width, titleH)
    if (order.orderNumber) out.centered(String(order.orderNumber), width, height)
    out.rule(width, height)
    ;(order.items || []).forEach((item: any) => {
        const qty = String(item.amount ?? item.qty ?? 1)
        out.line(`${qty} ${item.printName || item.name || ''}`, 0, width, height)
    })
    return [out.finish(beep(printer))]
}

export function deliverySlipText(order: any, _store: any, printer: any, style: SlipStyle): Uint8Array[] {
    if (!order?.deliverySlip || !order?.isDeliveryOrder) return []
    const { out, width, height, titleH } = open(printer, style)
    if (order.orderNumber) out.centered(String(order.orderNumber), width, titleH)
    const initials = guestInitials(order.guestName)
    if (initials) out.boldLine(initials, 1, width, titleH)
    deliveryAddressLines(order.deliveryAddress).forEach((line) => out.centered(line, width, height))
    out.rule(width, height)
    writeItems(out, order.items || [], width, height, false)
    return [out.finish(beep(printer))]
}

export function orderSlipText(order: any, store: any, printer: any, style: SlipStyle): Uint8Array {
    const { out, width, height, titleH } = open(printer, style)
    if (store?.message_top) out.centered(store.message_top, width, height)
    out.centered(store?.name || '', width, titleH)
    if (store?.address) out.centered(store.address, width, height)
    if (store?.phone) out.centered(store.phone, width, height)
    out.centered(clock(store?.timeZone, 'M/d/yyyy, hh:mm a'), width, height)
    const guests = order.guestCount?.total
    out.centered(
        [`Table: ${order.tableName || ''}`, guests ? `[#${guests}]` : '', order.counter ? `ID: ${order.counter}` : ''].filter(Boolean).join('  '),
        width,
        height
    )
    out.rule(width, height)
    writeItems(out, order.items || [], width, height, true)
    const subtotal = Number(order.total || 0)
    out.rule(width, height)
    out.pair('Sub-Total', `$${subtotal.toFixed(2)}`, width, height)
    if (store?.message_bottom) out.centered(store.message_bottom, width, height)
    if (store?.message_custom) out.centered(store.message_custom, width, height)
    return out.finish(beep(printer))
}

export function sessionSlipText(session: any, store: any, printer: any, style: SlipStyle): Uint8Array {
    const { out, width, height, titleH } = open(printer, style)
    if (store?.message_top) out.centered(store.message_top, width, height)
    out.centered(store?.name || '', width, titleH)
    if (store?.phone) out.centered(store.phone, width, height)
    if (store?.address) out.centered(store.address, width, height)
    out.centered(`Table: ${session.tableName || ''}`, width, titleH)
    const guestLine = guestSummary(session.guests, storeGuestGroups(store))
    if (guestLine) out.centered(guestLine, width, height)
    const url = `${window.location.origin}/order?s=${session.id || ''}`
    out.qr(url)
    if (store?.message_bottom) out.centered(store.message_bottom, width, height)
    return out.finish(beep(printer))
}

export function salesReportText(report: any, store: any, printer: any, style: SlipStyle, start: Date, end: Date): Uint8Array {
    const { out, width, height, titleH } = open(printer, style)
    out.centered(store?.name || '', width, titleH)
    out.centered('POS SALES REPORT', width, titleH)
    out.centered(clock(store?.timeZone, 'M/d/yyyy, hh:mm a'), width, height)
    out.centered(`${format(start, 'M/d/yyyy, hh:mm a')} - ${format(end, 'M/d/yyyy, hh:mm a')}`, width, height)
    out.rule(width, height)
    out.pair('Orders', String(report.orderCount ?? 0), width, height)
    out.pair('Sales', money(report.totalSales), width, height)
    if (report.totalTaxes != null) out.pair('Taxes', money(report.totalTaxes), width, height)
    if (report.paymentBreakdown) {
        out.rule(width, height)
        Object.entries(report.paymentBreakdown).forEach(([method, row]: [string, any]) => {
            out.pair(method, money(row?.amount), width, height)
        })
    }
    return out.finish(beep(printer))
}

function money(value: number): string {
    return `$${Number(value || 0).toFixed(2)}`
}

function uniqueNotes(items: any[]): string[] {
    const notes: string[] = []
    items.forEach((item) => {
        if (item?.itemNote && !notes.includes(item.itemNote)) notes.push(item.itemNote)
    })
    return notes
}

function beep(printer: any) {
    return {
        enabled: Boolean(printer?.beep_enabled),
        count: printer?.number_of_beeps || 2,
    }
}
